import { type JSX, useEffect } from 'react';

import { useOperatingUnitOptions } from '../operating-units/use-operating-unit-options';

export interface OperatingUnitSelectProps {
  id: string;
  label: string;
  value: string;
  /** Reports `''` when the user picks the "all" option (filter mode) or clears the choice. */
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string | undefined;
  /** Filter mode: adds this option (value `''`) and never preselects a unit. */
  allLabel?: string;
  /** Offer only these units (e.g. the points where one customer holds a contract). */
  restrictTo?: readonly string[];
  /** Extra choice listed after the units (e.g. a read-only "all units" view); only offered while the select shows. */
  extraOption?: { value: string; label: string };
}

/**
 * `design.md` §9: reads `GET /operating-units/options` (active units within the
 * caller's scope). With a single unit there is nothing to choose, so the select
 * hides itself; as a form field it then preselects that unit (`design.md` §2 —
 * single-unit behavior stays the current one). A filter (`allLabel`) only hides.
 */
export function OperatingUnitSelect({
  id,
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  allLabel,
  restrictTo,
  extraOption,
}: OperatingUnitSelectProps): JSX.Element | null {
  const options = useOperatingUnitOptions().filter(
    (option) => restrictTo === undefined || restrictTo.includes(option.id),
  );
  const onlyUnit = options.length === 1 ? options[0] : undefined;
  const isFilter = allLabel !== undefined;

  useEffect(() => {
    if (onlyUnit !== undefined && !isFilter && value === '' && !disabled) {
      onChange(onlyUnit.id);
    }
  }, [onlyUnit, isFilter, value, disabled, onChange]);

  if (options.length <= 1) {
    return null;
  }

  const messageId = `${id}-message`;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-slate-900">
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        ) : null}
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={hasError}
        aria-describedby={hasError ? messageId : undefined}
        className={`h-11 rounded-md border px-3 text-sm disabled:bg-slate-50 ${hasError ? 'border-red-500' : 'border-slate-300'}`}
      >
        <option value="">{allLabel ?? 'Seleccioná una opción'}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
        {extraOption === undefined ? null : (
          <option value={extraOption.value}>{extraOption.label}</option>
        )}
      </select>
      {hasError ? (
        <p id={messageId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
